import variables from '@/config/variables';
import r from './core';

export default {
  user: {
    get(...args) {
      return r.get('me', ...args);
    },
    validate(accessToken) {
      return r.get('me', {
        headers: {
          'Authorization': `Token token="${accessToken}"`,
          'Accept-Language': 'en',
        },
      });
    },
    sendVerificationEmail() {
      return r.post('send_email_verification');
    },
    /**
     * Add or update an email address to the user account using the provided email address
     * if newEmail is falsey, a noChange key will be inserted to the payload. During noChange,
     * the existing email will be used and a verification email will be sent to the existing
     * email `primary_email`
     * @param {string} newEmail
     */
    updateEmail(data = {}) {
      const payload = {};
      if (!data.email) payload.noChange = true;
      else payload.email = data.email;

      return r.post('user_email_update', payload);
    },
    filmmaker: {
      create(data) {
        return r.post('film_maker_details', data);
      },
    },
  },
  dashboard: {
    list(...args) {
      return r.get('submitvideos', ...args);
    },
  },
  submission: {
    new() {
      return r.post('submitvideo', {
        rights_general: true,
        rights_advertising: true,
        rights_media: 'MEDIA_ALL',
      });
    },
    fastTrack(data) {
      return r.post('submitvideos/fast_track', data);
    },
    bulkFastTrack(data) {
      return r.post('submitvideos/bulk', data);
    },
    get(videoId) {
      return Promise.all([
        this.getSimple(videoId),
        this.getExtra(videoId),
      ])
        .then(responses => ({
          data: {
            ...responses[0].data,
            ...responses[1].data,
          },
        }));
    },
    getSimple(videoId) {
      return r.get(`submitvideo/${videoId}`);
    },
    getExtra(videoId) {
      return Promise.all([
        this.collaborators.list(videoId),
        this.attachments.list(videoId),
        this.image.list(videoId),
      ])
        .then((responses) => {
          const data = responses.map(response => response.data);

          return {
            data: {
              collaborators: data[0].collaborators,
              attachments: data[1],
              image: data[2],
            },
          };
        });
    },
    /**
     * Handle of updates of data by a submission
     * @param {string} videoId
     * @param {object} data to be uploaded
     */
    update(videoId, data) {
      if (data.id && Object.keys(data).length === 1) {
        // Nothing to update, just return the latest data.
        return this.get(videoId);
      } else {
        return r.put(`submitvideo/${videoId}`, data)
          .then(() => this.get(videoId));
      }
    },
    getUploadPolicy(videoId) {
      return r.get(`submitvideo/${videoId}/upload_policy`);
    },
    delete(videoId) {
      return r.get(`submitvideo/${videoId}/clear_uploads`)
        .then(() => r.delete(`submitvideo/${videoId}`));
    },
    collaborators: {
      list(videoId) {
        return r.get(`submitvideo/${videoId}/collaborators`);
      },
      add(videoId, data) {
        return r.post(`submitvideo/${videoId}/collaborators`, data);
      },
      get(videoId, collaboratorId) {
        return r.put(`submitvideo/${videoId}/collaborators`);
      },
      update(videoId, collaboratorId, data) {
        return r.put(`submitvideo/${videoId}/collaborators/${collaboratorId}`, data);
      },
      delete(videoId, collaboratorId) {
        return r.delete(`submitvideo/${videoId}/collaborators/${collaboratorId}`);
      },
    },
    image: {
      list(videoId) {
        return r.get(`submitvideo/${videoId}/images`)
          .then((response) => {
            response.data = response.data.map((item) => {
              const url = item.image.image_file_url || '';
              const name = url.split('/').splice(-1, 1)[0];
              delete item.image;

              return {
                ...item,
                url,
                name,
              };
            });

            return response;
          });
      },
      delete(videoId, fileId) {
        return r.delete(`submitvideo/${videoId}/image/${fileId}`);
      },
    },
    attachments: {
      list(videoId) {
        return r.get(`submitvideo/${videoId}/attachments`);
      },
      add(videoId, data) {
        return r.post(`submitvideo/${videoId}/attachments`, data);
      },
      update(videoId, fileId, data) {
        return r.put(`submitvideo/${videoId}/attachments/${fileId}`, data);
      },
      delete(videoId, fileId) {
        return r.delete(`submitvideo/${videoId}/attachments/${fileId}`);
      },
    },
    additionalAgreements(videoId) {
      return r.get(`submitvideo/${videoId}/additional_agreements`);
    },
    fastTrackAdditionalAgreements() {
      return r.get('/additional_agreements');
    },
  },
  support: {
    send(data) {
      const userName = data.name;

      return r.post('support', {
        subject: `Creator: ${userName} requested for suppport`,
        ...data,
      });
    },

    log(data) {
      if (process.env.NODE_ENV === 'development') {
        /* eslint no-console: 0 */
        console.log(data); // don't send tracker during development
        return Promise.resolve();
      }

      return r.post('https://tracker.viddsee.com/log', data, {
        headers: '',
      });
    },
  },
  agreement: {
    get() {
      return r.get('film_license_agreement/latest');
    },
    sign(data) {
      return r.post('film_license/sign', data);
    },

    getUpgradableLicense(data) {
      return r.get('submitvideos/license_upgradable', data);
    },
  },

  app: {
    getLocaleInformation() {
      return r.get(`${variables.BASE_URL}/ip_json`);
    },
  },

  survey: {
    postSurvey(surveyId, answers) {
      return r.post(
        `${variables.Survey.ENDPOINT}`,
        {
          survey_id: surveyId,
          survey_answers: answers,
        },
      );
    },
  },

  juree: {
    info(config) {
      return r.get('../v2/juree/info', config);
    },

    country({ countryName }, config) {
      return r.get(`../v2/juree/country/${countryName}`, config);
    },
  },

  // Webcall's own API
  updateAccessToken(accessToken) {
    r.updateAccessToken(accessToken);
  },
  getHeader() {
    return r.getHeader();
  },
};



// WEBPACK FOOTER //
// ./src/libs/webcall/index.js